import moment from "moment";
import { FC, useEffect, useMemo, useState } from "react";
import { Modal, ModalBody } from "reactstrap";
import {
  checkLotteryByPeriodApi,
  getPeriodLotteryByPeriodYearApi,
} from "../api/lottery";
import {
  ICheckLotteryResultResponse,
  IPeriodLotteryWithMainResult,
} from "../interface/lottery";
import { setAppLoading } from "../redux/app.reducer";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import useSnackbar from "../utils/snackbar";

const CheckLottery: FC = () => {
  const [openSnackbar] = useSnackbar({ position: "bottom-left" });
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [periodLottery, setPeriodLottery] = useState<
    IPeriodLotteryWithMainResult[]
  >([]);
  const activePeriod = useAppSelector((state) => state.auth.activePeriod);
  const listYear = useMemo(() => {
    if (activePeriod) {
      return Array.from({ length: 11 }).map(
        (_, i) => activePeriod.period.year - i
      );
    }
    return Array.from({ length: 11 }).map((_, i) => moment().year() - i);
  }, [activePeriod]);
  const [selectedYear, setSelectedYear] = useState<number>(
    activePeriod?.period.year ?? moment().year()
  );
  const [lotteryNumber, setLotteryNumber] = useState<string>("");
  const [selectedPeriod, setSelectedPeriod] =
    useState<IPeriodLotteryWithMainResult>();
  const [showModalResult, setShowModalResult] = useState<{
    open: boolean;
    result: ICheckLotteryResultResponse;
  }>({
    open: false,
    result: {} as ICheckLotteryResultResponse,
  });

  const getPeriodLotteryByPeriodYear = async (year: number) => {
    try {
      const { data } = await getPeriodLotteryByPeriodYearApi(year);
      setPeriodLottery(data ?? []);
      if ((data ?? []).length > 0) {
        setSelectedPeriod(data.find((e) => e.status === 1) ?? data[0]);
      } else {
        setSelectedPeriod(undefined);
      }
    } catch (e: any) {
      let msg;
      if (typeof e?.response?.data?.message === "string") {
        msg = e?.response?.data?.message;
      }
      openSnackbar(msg || "Error: Something wrong.");
    }
  };

  const checkLottery = async () => {
    try {
      setLoading(true);
      if (!selectedPeriod) {
        throw new Error("");
      }

      if (lotteryNumber.length === 0) {
        throw new Error("กรุณากรอกหมายเลขลอตเตอรี่");
      }

      let lotteryNumbers: string[] = [];

      for (const number of lotteryNumber.split(",")) {
        let n = number.split(" ").join("");
        if (n.length > 0) {
          lotteryNumbers.push(n);
        }
      }

      if (lotteryNumbers.length === 0) {
        throw new Error("กรอกหมายเลขลอตเตอรี่ไม่ถูกต้อง");
      }

      const { data } = await checkLotteryByPeriodApi({
        period: selectedPeriod.period,
        lotteryNumbers,
      });

      setLoading(false);
      setShowModalResult({
        open: true,
        result: data,
      });
    } catch (e: any) {
      setLoading(false);
      let msg;

      if (typeof e?.response?.data?.message === "string") {
        msg = e?.response?.data?.message;
      } else if (typeof e?.message === "string" && e?.message !== "") {
        msg = e?.message;
      }

      openSnackbar(msg || "Error: Something wrong.");
    }
  };

  useEffect(() => {
    getPeriodLotteryByPeriodYear(selectedYear);
    // eslint-disable-next-line
  }, [selectedYear]);

  useEffect(() => {
    dispatch(setAppLoading(loading));
    // eslint-disable-next-line
  }, [loading]);

  return (
    <div>
      <div className="row d-flex">
        <div className="col-12 ">
          <div className="card">
            <div className="card-body">
              <p className="text-muted font-weight-bold tx-13">
                สลากกินแบ่งรัฐบาล
              </p>
              <div className="row">
                <div className="col-auto d-none d-md-block">
                  <div className="btn btn-primary">
                    <h2 className="m-0">{selectedPeriod?.period.day ?? ""}</h2>
                    <h4 className="m-0">
                      {!!selectedPeriod
                        ? moment.monthsShort(selectedPeriod!.period.month - 1)
                        : ""}
                    </h4>
                  </div>
                </div>
                <div className="col">
                  <div className="row">
                    <div className="col-12 col-md-6">
                      <div
                        className="d-flex flex-column flex-md-row"
                        style={{
                          gap: ".5rem",
                          paddingLeft: "0.625rem",
                          paddingRight: "0.625rem",
                        }}
                      >
                        <div className="form-group" style={{ minWidth: 100 }}>
                          <label className="required">ปี</label>
                          <select
                            className="form-control"
                            value={selectedYear}
                            onChange={(e) => {
                              setSelectedYear(+e.target.value);
                            }}
                          >
                            {listYear.map((year) => (
                              <option key={year} value={year}>
                                {year + 543}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group w-100">
                          <label className="required">งวดประจำวันที่</label>
                          <select
                            className="form-control"
                            value={selectedPeriod?.id}
                            onChange={(e) => {
                              setSelectedPeriod(
                                periodLottery.find(
                                  (ee) => ee.id === e.target.value
                                )
                              );
                            }}
                          >
                            {periodLottery.map((val) => (
                              <option key={val.id} value={val.id}>
                                งวดวันที่ {val.period.day}{" "}
                                {moment.months(val.period.month - 1)}{" "}
                                {val.period.year + 543}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="form-group">
                        <label className="required">หมายเลขลอตเตอรี่</label>
                        <div className="input-group col">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="111111, 222222, ..."
                            value={lotteryNumber}
                            onChange={(e) => {
                              setLotteryNumber(e.target.value);
                            }}
                          />
                          <span
                            className="input-group-append"
                            style={{ height: 38 }}
                          >
                            <button
                              className="btn btn-sm btn-primary"
                              type="button"
                              onClick={() => {
                                checkLottery();
                              }}
                            >
                              <i className="fa fa-search " />
                            </button>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="col">
                        <p className="text-muted font-weight-bold tx-13">
                          ตรวจสลากกินแบ่งรัฐบาล{" "}
                          {!!selectedPeriod
                            ? `${selectedPeriod!.period.day} ${moment.months(
                                selectedPeriod!.period.month - 1
                              )} ${selectedPeriod!.period.year + 543}`
                            : ""}
                        </p>
                        <div className="row">
                          <div className="col-md text-center">
                            <small className="lotto-check__item text-muted font-weight-bold">
                              รางวัลที่ 1
                            </small>
                            <b className="lotto__number">
                              {selectedPeriod?.first[0].value ?? ""}
                            </b>
                          </div>
                          <div className="col-md text-center">
                            <small className="lotto-check__item text-muted font-weight-bold">
                              เลขหน้า 3 ตัว
                            </small>
                            <b className="lotto__number">
                              {selectedPeriod?.first3[0].value ?? ""}
                            </b>
                            <b className="lotto__number">
                              {selectedPeriod?.first3[1].value ?? ""}
                            </b>
                          </div>
                          <div className="col-md text-center">
                            <small className="lotto-check__item text-muted font-weight-bold">
                              เลขท้าย 3 ตัว
                            </small>
                            <b className="lotto__number">
                              {selectedPeriod?.last3[0]?.value ?? ""}
                            </b>
                            <b className="lotto__number">
                              {selectedPeriod?.last3[1]?.value ?? ""}
                            </b>
                          </div>
                          <div className="col-md text-center">
                            <small className="lotto-check__item text-muted font-weight-bold">
                              เลขท้าย 2 ตัว
                            </small>
                            <b className="lotto__number">
                              {selectedPeriod?.last2[0]?.value ?? ""}
                            </b>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        size="md"
        isOpen={showModalResult.open}
        toggle={() => {
          setShowModalResult((prev) => ({
            ...prev,
            open: false,
          }));
        }}
        centered={true}
      >
        <div className="modal-title modal-header">
          <h5 className="modal-title">ตรวจผลลอตเตอรี่</h5>
          <button
            type="button"
            onClick={() => {
              setShowModalResult((prev) => ({
                ...prev,
                open: false,
              }));
            }}
            className="close"
            data-dismiss="modal"
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <ModalBody>
          {showModalResult.result?.totalReward > 0 ? (
            <div>
              <div className="text-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="160.506"
                  height="184.414"
                  viewBox="0 0 160.506 184.414"
                >
                  <g
                    id="Group_9969"
                    data-name="Group 9969"
                    transform="translate(-126.747 -219.086)"
                  >
                    <g id="winner" transform="translate(168.839 257.516)">
                      <path
                        id="Path_17069"
                        data-name="Path 17069"
                        d="M482,159.6v0Z"
                        transform="translate(-410.15 -135.809)"
                        fill="#3b9b24"
                      />
                      <path
                        id="Path_17070"
                        data-name="Path 17070"
                        d="M495.934,143.52v0Z"
                        transform="translate(-422.007 -122.126)"
                        fill="#3b9b24"
                      />
                      <g
                        id="Group_9958"
                        data-name="Group 9958"
                        transform="translate(0 0)"
                      >
                        <path
                          id="Path_17071"
                          data-name="Path 17071"
                          d="M62.288,195.2,50.748,206.74q-.593.593-1.23,1.121a11.814,11.814,0,1,0-22.714,0q-.636-.529-1.23-1.121L14.035,195.2A8.224,8.224,0,0,0,2.4,206.831l10.547,10.546a29.359,29.359,0,0,1,8.6,20.761,2.236,2.236,0,0,0,2.236,2.236H52.535a2.236,2.236,0,0,0,2.236-2.236,29.359,29.359,0,0,1,8.6-20.761l10.548-10.546A8.224,8.224,0,0,0,62.288,195.2Zm-24.127,2.064a7.352,7.352,0,0,1,7.344,7.344c-.369,9.729-14.32,9.727-14.688,0a7.352,7.352,0,0,1,7.344-7.344Zm32.595,6.405L60.209,214.215A33.865,33.865,0,0,0,50.372,235.9H25.95a33.866,33.866,0,0,0-9.836-21.687L5.567,203.669a3.752,3.752,0,1,1,5.306-5.307L22.412,209.9a22.12,22.12,0,0,0,15.744,6.521h.011A22.12,22.12,0,0,0,53.91,209.9L65.45,198.362a3.752,3.752,0,1,1,5.306,5.307Z"
                          transform="translate(0 -164.052)"
                          fill="#3b9b24"
                        />
                        <path
                          id="Path_17072"
                          data-name="Path 17072"
                          d="M146.836,96.4a2.236,2.236,0,0,0-2.236,2.236c0,2.579,2.273,3.716,3.631,4.395a9.3,9.3,0,0,1,1.174.655,2.237,2.237,0,0,0,4.457-.26c-.134-3.284-2.974-3.85-4.8-5.05a2.237,2.237,0,0,0-2.221-1.976Z"
                          transform="translate(-123.045 -82.03)"
                          fill="#3b9b24"
                        />
                        <path
                          id="Path_17073"
                          data-name="Path 17073"
                          d="M421.364,6.631a9.29,9.29,0,0,1,1.174.655,2.237,2.237,0,0,0,4.457-.26c-.134-3.284-2.974-3.85-4.8-5.05a2.236,2.236,0,0,0-4.457.26C417.733,4.815,420.006,5.952,421.364,6.631Z"
                          transform="translate(-355.463 0)"
                          fill="#3b9b24"
                        />
                        <path
                          id="Path_17074"
                          data-name="Path 17074"
                          d="M2.236,59.857a2.236,2.236,0,0,0,2.236-2.236c0-.414,1.533-1.181,2.357-1.593,2.038-1.019,4.828-2.414,4.828-5.592a2.236,2.236,0,1,0-4.472,0c0,.414-1.533,1.181-2.356,1.592C2.791,53.047,0,54.443,0,57.621A2.236,2.236,0,0,0,2.236,59.857Z"
                          transform="translate(0 -41.015)"
                          fill="#3b9b24"
                        />
                        <path
                          id="Path_17075"
                          data-name="Path 17075"
                          d="M323.569,91.99a2.236,2.236,0,0,0,2.236-2.236c0-.414,1.533-1.181,2.357-1.593,2.038-1.019,4.829-2.414,4.829-5.592a2.236,2.236,0,1,0-4.472,0c0,.414-1.533,1.181-2.356,1.592-2.038,1.019-4.829,2.414-4.829,5.593A2.236,2.236,0,0,0,323.569,91.99Z"
                          transform="translate(-273.433 -68.358)"
                          fill="#3b9b24"
                        />
                        <path
                          id="Path_17076"
                          data-name="Path 17076"
                          d="M130.967,5.433l.138.069a2.236,2.236,0,0,0,4.3-.872,3.673,3.673,0,0,0-2.434-3.2l-.138-.069a2.236,2.236,0,0,0-4.3.872A3.673,3.673,0,0,0,130.967,5.433Z"
                          transform="translate(-109.373 0)"
                          fill="#3b9b24"
                        />
                        <path
                          id="Path_17077"
                          data-name="Path 17077"
                          d="M465.934,130.832c.038,2.085,1.734,2.833,2.571,3.267a2.236,2.236,0,0,0,4.3-.872,3.673,3.673,0,0,0-2.433-3.2l-.138-.069A2.24,2.24,0,0,0,465.934,130.832Z"
                          transform="translate(-396.479 -109.436)"
                          fill="#3b9b24"
                        />
                        <path
                          id="Path_17078"
                          data-name="Path 17078"
                          d="M275.37,22.934a2.236,2.236,0,0,0,2.06-1.364l.138-.069A3.673,3.673,0,0,0,280,18.3a2.236,2.236,0,0,0-4.3-.872l-.138.069a3.673,3.673,0,0,0-2.434,3.2A2.236,2.236,0,0,0,275.37,22.934Z"
                          transform="translate(-232.419 -13.672)"
                          fill="#3b9b24"
                        />
                      </g>
                    </g>
                    <g
                      id="_306711"
                      data-name="306711"
                      transform="translate(126.55 1441.001)"
                    >
                      <path
                        id="Path_17079"
                        data-name="Path 17079"
                        d="M66.206-1221.725c-.276.113-1.856.577-3.512,1.054a28.679,28.679,0,0,0-5.769,2.032,7.837,7.837,0,0,0-4.415,4.954l-.314,1.041-1.467.916c-.8.514-2.3,1.48-3.324,2.145-1.317.865-1.781,1.116-1.593.865a19.5,19.5,0,0,0,2.772-5.117c.4-1.166,1.781-6.334,1.681-6.334a47.807,47.807,0,0,0-6.133,3.938,27.8,27.8,0,0,0-3.926,3.951,14.419,14.419,0,0,0-2.17,4.227,7.771,7.771,0,0,0,.013,4.189l.276.815-.364.3c-1.518,1.292-3.725,3.361-5.117,4.816l-1.668,1.743.615-1.192c1.731-3.349,2.283-7.174,1.806-12.379-.125-1.342-.263-2.508-.3-2.6s-.539.414-1.2,1.254c-5.581,6.923-6.635,8.867-7.1,13.069-.3,2.847.376,6.685,1.2,6.81.163.025.589-.251,1.166-.727l.916-.778-1.116,1.493a71.61,71.61,0,0,0-5.243,8.09c-.176.351-.339.6-.376.577a3.063,3.063,0,0,1,.075-.74,23.042,23.042,0,0,0,.251-5.042c-.151-2.421-.376-3.324-2.22-9.118-.389-1.2-.74-2.245-.79-2.3-.1-.113-.276.426-1.756,5.055-2.345,7.362-2.784,10.3-1.994,13.508a14.928,14.928,0,0,0,1.781,4.214l.9,1.605.364-.414c.2-.226.326-.326.288-.226s-.288.752-.564,1.455c-.665,1.63-1.38,3.725-1.969,5.694-.251.865-.489,1.656-.527,1.756s-.2-.376-.351-1.066a27.336,27.336,0,0,0-3.625-8.516c-.64-.978-1.555-2.408-2.019-3.173-.84-1.4-1.242-1.906-1.392-1.756a16.971,16.971,0,0,0-.188,2.37c-.063,1.267-.163,3.085-.238,4.051a56.323,56.323,0,0,0,.063,8.215c.577,4.277,1.881,6.735,4.478,8.416a5.31,5.31,0,0,0,1.066.564c.339.088.339-.013.1,2.157-.226,2.095-.439,5.456-.439,6.986v1.129l-.7-1.3a47.137,47.137,0,0,0-5.13-7.626,39.377,39.377,0,0,0-5.293-4.3c-.138,0-.176.088-.125.276.163.765.878,4.415,1.229,6.309,1.455,7.914,3.65,12.028,7.889,14.812a18.131,18.131,0,0,0,2.722,1.492c.013,0,.138.765.288,1.693.339,2.132.9,4.841,1.367,6.66a5.972,5.972,0,0,1,.276,1.292c-.05-.063-.351-.452-.665-.865-1.4-1.856-2.483-2.7-9.82-7.613A34.516,34.516,0,0,0,.2-1139.662c-.05.151,4.616,9.582,5.682,11.489a18.36,18.36,0,0,0,3.512,4.766,17.331,17.331,0,0,0,4.854,3.5,15.975,15.975,0,0,0,4.151,1.33c.414,0,.589.05.589.163a83.312,83.312,0,0,0,4.829,9.181c.2.3.088.238-.627-.339a28.223,28.223,0,0,0-5.268-3.173,30.068,30.068,0,0,0-6.948-2.508c-1.242-.313-2.872-.79-3.612-1.053-2.107-.752-2.145-.6-.376,1.518.7.853,1.944,2.4,2.759,3.437,2.8,3.6,7.237,7.525,9.933,8.78a25.364,25.364,0,0,0,5,1.492,10.375,10.375,0,0,0,2.784.138,11.165,11.165,0,0,0,1.844-.188c.276-.113.4-.013,1.3,1.028a73.349,73.349,0,0,0,7.814,7.638c.464.376.1.226-2.007-.828-2.408-1.217-2.659-1.3-4.013-1.555-5.13-.953-16.556-2.019-16.556-1.555,0,.427,7.952,6.033,10.247,7.212a33.393,33.393,0,0,0,8.065,2.784,19.523,19.523,0,0,0,7.4-.389c.376-.113,1.154-.4,1.731-.64,1.028-.439,1.028-.452.815-.7-.138-.163.013-.1.389.163a80.645,80.645,0,0,0,10.51,5.644,2.781,2.781,0,0,1,.69.339,11.025,11.025,0,0,1-1.355-.176,47.381,47.381,0,0,0-8.453-.05c-1.254.15-3.073.477-7.839,1.38-1.417.263-3.487.652-4.6.853a8.5,8.5,0,0,0-2.007.464c.063.176,9.758,3.048,11.438,3.386a26.99,26.99,0,0,0,6.923.539c4.816-.125,7.676-1.066,10.31-3.4l1.179-1.028,1.681.477c1.743.489,5.017,1.229,6.672,1.492l.916.151-.916.238c-2.446.64-3.474,1.091-10.836,4.753-5.393,2.671-7.337,3.713-7.337,3.9,0,.263,7.325-.025,10.535-.4a34.783,34.783,0,0,0,10.6-3.073,13.729,13.729,0,0,0,4.691-3.737l.8-.966h4.929l1.743,1.643c3.273,3.073,3.75,3.324,9.218,4.979,3.449,1.041,4.1,1.141,11.087,1.605,5.33.351,5.519.339,4.728-.326a30.228,30.228,0,0,0-4.59-2.32,16.966,16.966,0,0,1-3.386-1.756,38.3,38.3,0,0,0-10.209-4.239l-1.154-.3,1.794-.339a69.72,69.72,0,0,0,7.375-1.818c.865-.276,1.442-.414,1.292-.3-.426.288-.364.7.188,1.191a16.628,16.628,0,0,0,7.7,3.437,36.029,36.029,0,0,0,8.391-.05,101.04,101.04,0,0,0,11.438-3.361c.452-.238.426-.5-.063-.665-.627-.188-5.13-1.079-7.939-1.568-5.569-.966-8.654-1.279-10.686-1.079a39.655,39.655,0,0,0-5.544.941c-.64.176-1.192.313-1.242.313s.79-.364,1.856-.79a68.683,68.683,0,0,0,26.426-19.465c.514-.615.527-.627.5-.251-.038.6.426.753,2.358.753a16.9,16.9,0,0,0,5.519-.966,15.611,15.611,0,0,0,4.7-2.533,25.307,25.307,0,0,0,7.4-7.287,22.972,22.972,0,0,1,3.738-4.252c.439-.364.477-.615.075-.677a15.14,15.14,0,0,0-2.182.527c-1.066.313-2.747.765-3.712,1a24.9,24.9,0,0,0-5.406,1.957,32.989,32.989,0,0,0-7.6,4.9c-.978.891-1,.9-.64.364a75.1,75.1,0,0,0,5.556-10.184,2.814,2.814,0,0,1,.364-.728.9.9,0,0,1,.3.2c.288.276,1.079.125,2.822-.514a18.451,18.451,0,0,0,8.541-6.258,121.518,121.518,0,0,0,6.284-12c.627-1.417.753-1.869.527-1.869-.263,0-5.82,3.512-8.93,5.656a20.775,20.775,0,0,0-5.945,5.569c-.326.477-.6.891-.615.9s.163-.652.389-1.505a79.816,79.816,0,0,0,1.856-9.005c.063-.539.088-.564.414-.514a3.064,3.064,0,0,0,1.317-.452,14.793,14.793,0,0,0,6.9-6.986c1.267-2.546,1.881-4.7,2.835-9.908.351-1.869.753-3.775.891-4.239.276-.89.288-1.354.05-1.43s-1.467,1.028-3.813,3.424c-4.578,4.666-6.321,7.049-7.538,10.322l-.351.953-.013-1.693a81.491,81.491,0,0,0-.715-9.783l-.075-.314.3.276c.276.238.339.251.765.113a7.506,7.506,0,0,0,2.521-1.969c2.446-2.621,3.487-7.35,3.123-14.06-.151-2.847-.514-7.576-.6-7.864-.113-.376-4.39,6.81-5.694,9.545-1.1,2.358-1.242,3.048-1.267,6.371l-.013,2.32-.3-1.192c-.539-2.182-1.066-4.039-1.643-5.82-.615-1.919-2.233-6.108-2.809-7.249-.615-1.217-.4-.991.389.427.84,1.492,1.1,1.731,1.568,1.43,1.141-.753,2.947-5.23,3.161-7.814a10.637,10.637,0,0,0-.075-1.881,77.166,77.166,0,0,0-2.8-10.36c-.8-2.4-.941-2.722-1.179-2.722-.389,0-2.157,4.264-2.634,6.4-.439,1.906-.439,9.7,0,10.874a1.8,1.8,0,0,1,.138.539,14.816,14.816,0,0,1-.753-1.342,74.014,74.014,0,0,0-8.679-12.693,90.806,90.806,0,0,0-7.174-7.224c-1.066-.9-1.141-1-.878-1.066.514-.125.665-.665.715-2.433a7.118,7.118,0,0,0-1.116-4.8,30.348,30.348,0,0,0-6.259-6.635,32.559,32.559,0,0,0-4.478-2.809c-.288,0-.188.853.339,2.847,1.2,4.641,1.957,6.221,4.239,8.917.652.765.677.815.276.552-.991-.64-1.819-1.116-4.39-2.521l-2.7-1.468-.1-.778c-.326-2.659-2.508-5.2-5.744-6.723a85.488,85.488,0,0,0-8.892-2.784c-.426-.075-.5-.05-.5.138,0,.577,3.411,4.992,5.456,7.036a12.479,12.479,0,0,0,6.534,3.625l1.3.288,2.809,1.932c1.543,1.053,2.8,1.931,2.772,1.956a3.277,3.277,0,0,1-.64-.1,17.448,17.448,0,0,0-5.117-.063,45.64,45.64,0,0,0-8.566,2.408c-1.618.778-1.154.966,3.612,1.48,1.618.163,3.612.4,4.44.514,3.261.426,10,.351,11.413-.151.6-.2.552-.238,2.922,1.756,2.095,1.743,2.772,2.371,2.471,2.27a29.3,29.3,0,0,0-10.673-1.192c-2.145.113-2.809.263-2.809.627,0,.276.288.464,1.568,1,.552.226,2.559,1.141,4.452,2.019,6.045,2.784,8.2,3.437,12.254,3.7l1.3.088.477.577c1.267,1.543,4.026,5.28,4.026,5.468a2.009,2.009,0,0,1-.589-.364,25.822,25.822,0,0,0-4.691-2.37c-3.926-1.493-8.391-2.508-8.391-1.919a6.754,6.754,0,0,0,1.292,1.3c2.007,1.743,7.864,6.572,8.955,7.375a32.936,32.936,0,0,0,7.425,4.264l.8.226.439.953c1.066,2.345,2.822,7,2.5,6.647-1.668-1.806-2.182-2.333-3.01-3.085a16.921,16.921,0,0,0-2.007-1.593c-2.659-1.781-7.764-4.365-8.065-4.076-.125.138.3.79,1.279,1.919a41.991,41.991,0,0,1,4.917,7.086,25.369,25.369,0,0,0,1.63,2.5,31.822,31.822,0,0,0,6.986,6.371,3.113,3.113,0,0,1,.878.665c.125.414.7,4.716.878,6.647.176,1.894.238,3.926.1,3.324a18.264,18.264,0,0,0-2.784-6.12c-1-1.254-7.989-8.328-8.115-8.215-.2.213,2.7,9.419,3.963,12.58a32.756,32.756,0,0,0,6.083,9.958l.778.828-.1,1a80.09,80.09,0,0,1-1.329,8.341c-.05.2-.138-.151-.238-.991-.364-2.922-1.091-4.816-3.487-8.968-2.22-3.875-3.738-6.146-4.013-6.058-.226.075-.226,1.2-.013,4.791.314,5.381.677,9.8.953,11.664a22.548,22.548,0,0,0,3.411,8.19l.589.8-.7,1.706c-1.079,2.621-4.289,9.03-3.888,7.776.038-.125.138-.853.213-1.618a35.015,35.015,0,0,0-.263-7.638c-1-5.456-2.358-9.231-3.048-8.541a6.486,6.486,0,0,0-.288,1.58,84.186,84.186,0,0,1-1.731,8.378,33.516,33.516,0,0,0-1.166,7.651,14.239,14.239,0,0,0,.928,6.146l.539,1.618-.652.828c-.853,1.091-3.01,3.625-3.963,4.666l-.753.815.3-1.317a29.584,29.584,0,0,0,.715-8.09c0-2.019-.088-4.39-.176-5.33l-.176-1.693-1.2,2.32c-1.731,3.323-3.023,5.644-3.951,7.049-2.546,3.9-3.11,5.932-3.386,12.229l-.088,1.906-1.819,1.279a52.248,52.248,0,0,1-5.268,3.311,2.477,2.477,0,0,1,.389-.652,28.2,28.2,0,0,0,3.662-8.428,49.211,49.211,0,0,0,.916-5.305c-.063-.188-6.748,6.284-8.654,8.378-3.863,4.252-5.054,6.283-5.544,9.444l-.151.941-1.819.6c-1.58.514-5.631,1.643-5.631,1.555,0-.013.4-.364.9-.765a27.765,27.765,0,0,0,6.045-7.9,9.71,9.71,0,0,0,.8-2.634,15.572,15.572,0,0,0-1.731.778c-1.819.865-3.06,1.38-5.832,2.421-3.487,1.292-5.042,2.3-6.923,4.427-1.743,1.957-2.885,3.9-2.759,4.666l.063.4-.753.088c-.426.038-2.27.075-4.114.075-2.534,0-3.386-.037-3.474-.163a12.987,12.987,0,0,1-.652-1.442,11.746,11.746,0,0,0-3.411-4.866c-1.693-1.38-3.161-2.12-7.513-3.763-1.2-.452-3.085-1.267-4.164-1.806a11.822,11.822,0,0,0-1.982-.878,13.857,13.857,0,0,0,1.028,2.822,31.259,31.259,0,0,0,6.647,8.19c.339.251.564.477.5.5a64.479,64.479,0,0,1-7.362-2.019l-.5-.176-.088-.916c-.288-2.835-1.43-4.979-4.315-8.152-1.467-1.618-9.733-10.046-9.8-10a33.988,33.988,0,0,0,.953,5.268c1.091,4.252,2.17,6.8,3.938,9.231l.489.665-.6-.351c-3.587-2.069-7.024-4.4-7-4.766.013-.138.088-.728.188-1.342.489-3.261-1.091-8.892-3.712-13.132-1.267-2.057-2.408-4.076-3.65-6.472a23.8,23.8,0,0,0-1.292-2.308,57.121,57.121,0,0,0-.263,7.4c0,5.268.251,7.287,1.141,9.08l.376.765-.64-.627c-.978-.941-3.474-3.75-4.616-5.18l-1-1.254.364-.727a19.475,19.475,0,0,0,1.329-6.51c.1-2.245-.326-5.192-1.467-10.1a61.922,61.922,0,0,1-1.63-8.265c0-.552-.176-.891-.376-.69a18.17,18.17,0,0,0-1.493,3.738c-.452,1.605-1.141,4.741-1.543,7.036a19.063,19.063,0,0,0,.063,6.848c.188,1.016.364,1.944.414,2.069a24.392,24.392,0,0,1-2.207-3.85c-1.066-2.107-1.743-3.574-2.571-5.631l-.539-1.3.426-.326c1.392-1.041,3.411-5.142,3.876-7.876.564-3.311,1.505-16.568,1.192-16.882-.339-.339-2.132,2.17-4.277,6.008-2.433,4.365-3.186,6.7-3.361,10.41l-.075,1.756-.288-1.192a73.117,73.117,0,0,1-1.53-8.654c-.075-.727-.163-1.48-.188-1.681-.05-.313-.038-.339.125-.2.276.226.552-.063,2.258-2.32a48.326,48.326,0,0,0,3.951-5.907c.376-.8,1.706-4.377,1.982-5.318.063-.238.364-1.166.64-2.057.753-2.345,1.982-6.447,2.12-7.074.075-.289.075-.527.025-.527a75.722,75.722,0,0,0-7.174,7.024,19.313,19.313,0,0,0-3.926,8.315,4.342,4.342,0,0,1-.263.928c-.088-.075.2-5.1.376-6.8.326-3.073.727-5.456.916-5.456a6.948,6.948,0,0,0,2.37-1.417c3.085-2.308,5.255-4.8,9.143-10.56,1.016-1.518,2.5-3.612,3.286-4.653a15.033,15.033,0,0,0,1.417-2.094c0-.276-.4-.138-1.969.652a58.879,58.879,0,0,0-8.629,5.356,23.106,23.106,0,0,0-3.173,3.612,3.9,3.9,0,0,1-.527.677,78.124,78.124,0,0,1,2.835-7.575l.564-1.254.727-.151c1.329-.276,4.365-1.894,7.287-3.875a45.983,45.983,0,0,0,5.33-4.177c.9-.8,2.37-2.044,3.261-2.784,1.957-1.593,2.345-1.969,2.3-2.22-.063-.313-1.279-.226-3.173.226a28.48,28.48,0,0,0-10.147,4.39c-.539.389-1.1.79-1.242.891-.539.389,3.4-5.167,4.728-6.647.326-.364.351-.376,1.53-.376a26.149,26.149,0,0,0,11.025-2.91c1.179-.552,3.311-1.53,4.753-2.182s2.7-1.242,2.784-1.317c.3-.251.2-.577-.226-.74-1.116-.389-7.049-.364-9.632.05a22.4,22.4,0,0,0-3.863,1.016l-.464.188.627-.564c.878-.8,3.236-2.784,4.139-3.474l.753-.589,1,.288c2.471.715,6.058.74,11.413.075,4.177-.514,6.459-.753,7.325-.765.79,0,1.53-.213,1.618-.464.113-.376-4.126-1.982-6.823-2.584-.753-.176-1.919-.376-2.6-.464a28.354,28.354,0,0,0-6.3.188,5.091,5.091,0,0,1-.778.15,7.907,7.907,0,0,1,.815-.665c.464-.351,1.706-1.292,2.772-2.094a12.274,12.274,0,0,1,2.032-1.38,6.624,6.624,0,0,0,1.43-.075c2.947-.313,5.117-1.53,8.152-4.578,3.123-3.135,5.167-5.97,4.565-6.346C66.8-1221.951,66.821-1221.951,66.206-1221.725Z"
                        transform="translate(0 0)"
                        fill="#4f8941"
                      />
                      <path
                        id="Path_17080"
                        data-name="Path 17080"
                        d="M1016.975-1147.807c.025.928.088,2.935.125,4.453a33.3,33.3,0,0,0,.577,6.559,16.841,16.841,0,0,0,2.082,4.4c1.041,1.58,4.064,4.528,4.641,4.528.953,0,1.555-5.757.9-8.717a18.809,18.809,0,0,0-3.85-7.463c-.8-1.028-3.487-4.277-4.2-5.067l-.351-.389Z"
                        transform="translate(-889.187 -63.333)"
                        fill="#4f8941"
                      />
                      <path
                        id="Path_17081"
                        data-name="Path 17081"
                        d="M962.812-220.709c-7.789.552-10.134.84-12.931,1.543a24.179,24.179,0,0,0-9.632,4.866c-1.2.953-1.605,1.618-1.329,2.157.477.916,4.4,1.768,7.262,1.58a25.782,25.782,0,0,0,8.177-2.195c2.8-1.317,11.413-6.936,11.677-7.626a.262.262,0,0,0-.1-.3A15.425,15.425,0,0,0,962.812-220.709Z"
                        transform="translate(-820.914 -875.582)"
                        fill="#4f8941"
                      />
                    </g>
                    <text
                      id="WINNER-2"
                      data-name="WINNER"
                      transform="translate(158.5 395.5)"
                      fill="#8e0f14"
                      font-size="16"
                      font-family="SukhumvitSet-Bold, Sukhumvit Set"
                      font-weight="700"
                      letter-spacing="0.5em"
                    >
                      <tspan x="0" y="0">
                        WINNER
                      </tspan>
                    </text>
                  </g>
                </svg>
              </div>
              <div className="text-center mt-2">
                <div style={{ fontWeight: "bold", fontSize: 25 }}>
                  ยินดีด้วยคุณถูกรางวัล
                </div>
              </div>
              <div className="mt-2">
                <table className="table table-borderless">
                  <thead>
                    <th>
                      <td style={{ width: "20%" }}></td>
                      <td style={{ width: "40%" }}></td>
                      <td style={{ width: "40%" }}></td>
                    </th>
                  </thead>
                  <tbody>
                    {showModalResult.result?.results.map((result, index) => {
                      return (
                        <tr key={result.lotteryNumber}>
                          <td className="text-center">เลขสลาก {index + 1}</td>
                          <td className="text-center font-weight-bold">
                            {result.lotteryNumber}
                          </td>
                          <td
                            className={`text-center ${
                              result.statusCode === 1
                                ? "text-success font-weight-bold"
                                : ""
                            }`}
                          >
                            {result.statusCode === 0
                              ? "ไม่ถูกรางวัล"
                              : result.statusText.map((e) => <div>{e}</div>)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="font-weight-bold text-center">
              ไม่พบเลขสลากถูกรางวัล
            </div>
          )}
        </ModalBody>
      </Modal>
    </div>
  );
};

export default CheckLottery;
